import { useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent } from "@/design-system/components/ui";
import { ChartCard, EmptyState } from "./ChartCard";
import { chartTheme } from "./chartTheme";
import { useMetricsFinancial } from "./useMetricsQueries";
import { useMetricsFilter } from "./MetricsFilterContext";
import { buildPipelineDrillThroughUrl } from "@/lib/pipelineFilterEncoder";
import type {
  CommissionPoint,
  DealSizeBucket,
  FinancialStatCards,
} from "@/api/dealFlow/metrics";

// Financial tab (stat cards + 3 charts).

export function FinancialMetricsSection() {
  const { filter } = useMetricsFilter();
  const query = useMetricsFinancial(filter);
  if (query.isLoading) return <Loading />;
  if (query.error || !query.data) return <Error />;
  const { statCards, dealSizeDistribution, commissionRolling12Months } = query.data;

  return (
    <div className="flex flex-col gap-space-4" data-testid="metrics-loaded">
      <StatCards stats={statCards} />
      <div className="grid grid-cols-1 gap-space-4 lg:grid-cols-2">
        <ChartCard
          title="Deal size distribution"
          hint="Click a band to drill through to deals in that size band."
          testId="chart-size-dist"
        >
          <DistributionChart data={dealSizeDistribution} />
        </ChartCard>
        <ChartCard
          title="Commission booked (12 months)"
          hint="Sum of CommissionAmount per month, rolling 12 months."
          testId="chart-commission"
        >
          <CommissionChart data={commissionRolling12Months} />
        </ChartCard>
      </div>
    </div>
  );
}

function StatCards({ stats }: { stats: FinancialStatCards }) {
  const TrendIcon =
    stats.commissionTrend === "Up"
      ? ArrowUp
      : stats.commissionTrend === "Down"
        ? ArrowDown
        : Minus;
  const trendColor =
    stats.commissionTrend === "Up"
      ? "text-status-success"
      : stats.commissionTrend === "Down"
        ? "text-status-error"
        : "text-gray-mid";

  return (
    <div className="grid grid-cols-1 gap-space-3 md:grid-cols-2 xl:grid-cols-4">
      <StatCard label="Total submitted" value={formatUsd(stats.totalSubmittedUsd)} />
      <StatCard label="Total closed" value={formatUsd(stats.totalClosedUsd)} />
      <StatCard
        label={`Avg deal size · 90d (n=${stats.averageDealSampleSize})`}
        value={formatUsd(stats.averageDealSizeUsd)}
      />
      <Card>
        <CardContent>
          <div className="text-label uppercase text-gold-shadow tracking-label">
            Commission · last 30d
          </div>
          <div className="flex items-baseline gap-space-2 mt-space-1">
            <div className="font-display text-h3 font-bold">
              {formatUsd(stats.commissionLast30dUsd)}
            </div>
            <span className={`inline-flex items-center gap-1 ${trendColor}`}>
              <TrendIcon size={16} aria-hidden />
              <span className="text-small">
                vs {formatUsd(stats.commissionPrior30dUsd)} prior
              </span>
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent>
        <div className="text-label uppercase text-gold-shadow tracking-label">{label}</div>
        <div className="font-display text-h3 font-bold mt-space-1">{value}</div>
      </CardContent>
    </Card>
  );
}

function DistributionChart({ data }: { data: DealSizeBucket[] }) {
  const navigate = useNavigate();
  if (data.every((d) => d.dealCount === 0)) return <EmptyState label="No deals in this window." />;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ left: 16, right: 16 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis dataKey="band" stroke={chartTheme.axis} fontSize={11} />
        <YAxis stroke={chartTheme.axis} />
        <Tooltip />
        <Bar
          dataKey="dealCount"
          cursor="pointer"
          onClick={(entry) => {
            const band = (entry as unknown as DealSizeBucket).band;
            navigate(buildPipelineDrillThroughUrl({ dealSizeBand: band }));
          }}
        >
          {data.map((d) => (
            <Cell key={d.band} fill={chartTheme.primary} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function CommissionChart({ data }: { data: CommissionPoint[] }) {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ left: 16, right: 16 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis dataKey="label" stroke={chartTheme.axis} fontSize={11} />
        <YAxis stroke={chartTheme.axis} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
        <Tooltip formatter={(v: number) => formatUsd(v)} />
        <Line
          type="monotone"
          dataKey="amountUsd"
          stroke={chartTheme.primary}
          strokeWidth={2}
          dot={{ r: 3 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function formatUsd(v: number): string {
  return v.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function Loading() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-gray-mid">
      Loading financial metrics…
    </div>
  );
}
function Error() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-status-error">
      Failed to load financial metrics.
    </div>
  );
}
